import { Link, isRouteErrorResponse } from "react-router";
import { BrandMark } from "~/components/layout";

/**
 * Branded error page for parent-facing school routes — same visual system as
 * the 404 page, so a failed lookup never reads as a broken site.
 */
export function SchoolErrorPage({ error }: { error: unknown }) {
  let status = 0;
  if (isRouteErrorResponse(error)) {
    status = error.status;
  }

  const is404 = status === 404;
  const is429 = status === 429;

  const chip = is404
    ? "School code not found"
    : is429
      ? "Busy night · try again shortly"
      : "Temporary glitch · nothing lost";

  const title = is404
    ? "We can't find that school."
    : is429
      ? "One moment — the hallway is crowded."
      : "We couldn't open this school's page.";

  const lead = is404
    ? "No school is using this code right now. Double-check the link from your school — the code is the six digits in their invitation."
    : is429
      ? "Lots of families are booking at once. Give it a few seconds and try again — your spot isn't going anywhere."
      : "Our scheduling desk didn't answer just now. It's us, not you — the link is fine, so trying again usually does it.";

  return (
    <main className="nf">
      <div className="nf-wash" aria-hidden="true" />
      <div className="nf-blob nf-blob--a" aria-hidden="true" />
      <div className="nf-blob nf-blob--b" aria-hidden="true" />

      <div className="nf-inner">
        <div className="nf-brand nf-rise" style={{ animationDelay: "40ms" }}>
          <BrandMark variant="dark" endorseHref="https://timetosizzle.com" />
        </div>

        <div className="nf-hero">
          <p className="nf-chip nf-rise" style={{ animationDelay: "80ms" }}>
            {chip}
          </p>
          <h1 className="nf-title nf-rise" style={{ animationDelay: "140ms" }}>
            {title}
          </h1>
          <p className="nf-lead nf-rise" style={{ animationDelay: "200ms" }}>
            {lead}
          </p>

          <div className="nf-ctas nf-rise" style={{ animationDelay: "260ms" }}>
            {!is404 && (
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="btn btn-primary nf-cta-primary"
              >
                Try again
              </button>
            )}
            <Link
              to="/"
              className={is404 ? "btn btn-primary nf-cta-primary" : "btn btn-secondary nf-cta-secondary"}
            >
              Back to home
            </Link>
            <Link to="/support" className="nf-soft-link">
              Still stuck? Tell us.
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
